import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { Logo } from '../types/content';
import { useAppPrefix } from './AppPrefixContext';
import { useTournament } from './TournamentContext';

const pageTitles: Record<string, string> = {
  '/m/schedule': 'Schedule',
  '/m/leaderboard': 'Leaderboard',
  '/m/gallery': 'Gallery',
  '/m/more': 'More',
  '/m/about': 'About',
  '/m/registration': 'Register',
  '/m/history': 'History',
  '/m/sponsors': 'Partners',
  '/m/press': 'Press',
  '/m/prize-breakdown': 'Prize Breakdown',
  '/m/admin': 'Dashboard',
  '/m/admin/players': 'Players',
  '/m/admin/partners': 'Partners',
  '/m/admin/events': 'Events',
  '/m/admin/scores': 'Scores',
  '/m/admin/finance': 'Finance',
  '/m/admin/analytics': 'Analytics',
  '/m/admin/settings': 'Settings',
  '/m/admin/tournaments': 'Tournaments',
  '/m/admin/press': 'Press',
  '/m/admin/messaging': 'Messaging',
  '/m/invite': 'Invitation',
};

const adminSubPages = [
  '/m/admin/players',
  '/m/admin/partners',
  '/m/admin/events',
  '/m/admin/scores',
  '/m/admin/finance',
  '/m/admin/analytics',
  '/m/admin/settings',
  '/m/admin/tournaments',
  '/m/admin/press',
  '/m/admin/messaging',
];

export default function MobileTopBar() {
  const p = useAppPrefix();
  const { selectedTournament } = useTournament();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [headerLogo, setHeaderLogo] = useState<Logo | null>(null);
  const [tournamentYear, setTournamentYear] = useState<number | null>(null);
  const isHome = location.pathname === p || location.pathname === `${p}/`;
  const isMore = location.pathname === `${p}/more`;
  const isAdminSub = adminSubPages.map(s => s.replace('/m/', `${p}/`)).includes(location.pathname);
  const isLeaderboard = location.pathname === `${p}/leaderboard`;
  const isPlayerDetail = location.pathname.startsWith(`${p}/player/`);
  const isPromo = location.pathname === `${p}/promo`;
  const normalizedPath = location.pathname.replace(new RegExp(`^${p}`), '/m');
  const baseTitle = pageTitles[normalizedPath] ?? pageTitles[location.pathname] ?? '';
  const title = isLeaderboard && tournamentYear ? `Leaderboard · ${tournamentYear}` : baseTitle;

  useEffect(() => {
    fetchLogo();
    fetchSiteTitle();
  }, []);

  useEffect(() => {
    const tid = searchParams.get('tournamentId');
    if (isLeaderboard && tid) {
      fetchTournamentYear(tid);
    } else {
      setTournamentYear(null);
    }
  }, [isLeaderboard, searchParams]);

  const fetchTournamentYear = async (tid: string) => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('start_date')
        .eq('id', tid)
        .maybeSingle();
      if (data?.start_date) setTournamentYear(new Date(data.start_date).getFullYear());
    } catch {}
  };

  const fetchLogo = async () => {
    try {
      const { data } = await supabase
        .from('logos')
        .select('*')
        .eq('type', 'primary')
        .eq('location', 'header')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();
      setHeaderLogo(data);
    } catch {}
  };

  const fetchSiteTitle = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'site_title')
        .maybeSingle();
      if (data?.value) {
        document.title = String(data.value);
      }
    } catch {}
  };

  const handleBack = () => {
    if (isAdminSub) {
      navigate(`${p}/admin`);
    } else {
      navigate(p);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 flex items-center h-14 px-4 ${
        isPlayerDetail || isLeaderboard
          ? 'bg-golf-green border-b border-golf-green'
          : isPromo
            ? 'bg-golf-gray border-b border-white/10'
            : 'bg-white border-b border-gray-100'
      }`}
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {!isHome && !isMore ? (
        <button onClick={handleBack} className={`mr-2 p-1 -ml-1 transition-colors ${
          isPlayerDetail || isLeaderboard || isPromo ? 'text-white/80 hover:text-white' : 'text-gray-600 hover:text-golf-orange'
        }`}>
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
      ) : (
        <div className="w-7" />
      )}

      <div className="flex-1 flex items-center justify-center">
        {isHome ? (
          headerLogo ? (
            <img src={headerLogo.url} alt="Ibom Open" className="h-8 w-auto" />
          ) : (
            <span className="text-golf-orange font-display font-bold text-xl">Ibom Open</span>
          )
        ) : isPlayerDetail || isLeaderboard ? (
          <span className="font-display font-bold text-white text-lg">{isLeaderboard ? title : (selectedTournament?.name || 'Ibom Open')}</span>
        ) : isPromo ? (
          <span className="font-display font-bold text-white text-lg">{title}</span>
        ) : (
          <span className="font-display font-bold text-golf-gray text-lg">{title}</span>
        )}
      </div>

      <div className="w-7" />
    </header>
  );
}
